import { bool, number, string } from "prop-types";
import { createContext, useContext, useMemo, useRef } from "react";

import styled from "styled-components/macro";
import {
  backgroundColor,
  border,
  borderRadius,
  height,
  shadow,
  width,
} from "theme";
import { shadowVariants } from "theme/constants";

import { useFlocknoteAppState } from "Flocknote/state";

import { useIsSingleUserHousehold } from "HouseholdProfile/API/queryHooks";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrMovingDiv from "UI/FnrMovingDiv";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import useHeight from "Utilities/Hooks/useHeight";
import useIsMobile from "Utilities/Hooks/useIsMobile";
import useUrlParams from "Utilities/Hooks/useUrlParams";
import { useRemoveUrlParams, useUserID } from "Utilities/urlParamHooks";

import { useProfileSidebarContext } from "../ProfileSidebar/ProfileSidebar";

import PrimaryContents from "./components/PrimaryContents";
import UserMenu from "./components/UserMenu";
import { useLoadProfile } from "./stateHooks";

const UserChipContext = createContext();

/** @returns {UserChipContext} */
const useUserChipContext = () => useContext(UserChipContext) || {};

const Container = styled("div")`
  ${height}
  ${width}
  min-width: 140px;
  line-height: 0;

  @media screen and (max-width: 680px) {
    min-width: unset;
  }
`;

const SFnrMovingDiv = styled(FnrMovingDiv)`
  ${borderRadius}
  ${backgroundColor}
  ${border}
  min-width: 0;
  width: unset;
  transition: background-color 300ms, border 600ms;
  ${shadow}
  &:hover {
    background: ${({ $selected }) => ($selected ? "#FFF" : "#c7e2f2")};
  }
  min-height: 51px;
`;

const SFnrVerticalGap = styled(FnrVerticalGap)`
  min-width: fit-content;
  padding: 6px 4px;
`;

const UserChipContents = () => {
  const allContentsref = useRef(null);
  const allContentsHeight = useHeight(allContentsref);
  const primaryContentsRef = useRef(null);
  const primaryContentsHeight = useHeight(primaryContentsRef);
  const { isSelected, isMobile, isChild, userID } = useUserChipContext();
  const [{ profileDataIsLoading }] = useFlocknoteAppState();
  const loadProfile = useLoadProfile();
  const { setShowHousehold } = useProfileSidebarContext();
  const removeUrlParams = useRemoveUrlParams();

  const handleClick = () => {
    if (isSelected) {
      removeUrlParams("householdID");
      return;
    }
    if (isMobile) setShowHousehold(false);

    loadProfile();
  };

  return (
    <FnrButtonWrapper
      onClick={handleClick}
      fullWidth
      disabled={profileDataIsLoading}
    >
      <SFnrMovingDiv
        heightMotion
        $backgroundColor={isSelected ? "white" : "transparent"}
        $selected={isSelected}
        $color="lightGray"
        $noBorder
        $borderRadius={14}
        $noShadow={!isSelected}
        $shadowVariant={shadowVariants.subtle}
        $isChild={isChild}
      >
        <Container
          $height={
            isSelected ? allContentsHeight - 2 : primaryContentsHeight + 11
          }
          $width="100%"
          className={`UserChip UserChip_${userID}`}
        >
          <SFnrVerticalGap gap={4} ref={allContentsref}>
            <PrimaryContents isSelected={isSelected} ref={primaryContentsRef} />
            <UserMenu isSelected={isSelected} />
          </SFnrVerticalGap>
        </Container>
      </SFnrMovingDiv>
    </FnrButtonWrapper>
  );
};

/**
 * @param {HouseholdMember} param0
 */
const UserChip = (householdMemberProps) => {
  const activerUserID = useUserID();
  const { householdID, profileTab } = useUrlParams();
  const isSingleUserousehold = useIsSingleUserHousehold();
  const isSelected = useMemo(
    () =>
      (householdMemberProps.userID === activerUserID &&
        Boolean(!householdID) &&
        profileTab !== "giving") ||
      isSingleUserousehold,
    [
      householdMemberProps.userID,
      activerUserID,
      householdID,
      profileTab,
      isSingleUserousehold,
    ]
  );
  const isMobile = useIsMobile(680);

  return (
    <UserChipContext.Provider
      value={{ ...householdMemberProps, isSelected, isMobile }}
    >
      <UserChipContents />
    </UserChipContext.Provider>
  );
};

UserChip.propTypes = {
  photoUrl: string,
  firstName: string,
  userID: number,
  isHOH: bool,
  birthday: string,
};

export { useUserChipContext };
export default UserChip;
